import {
  getBadgeRule,
  isEntityNewByRule,
  type BadgeRuleRow,
} from "@/lib/badgeRules";

export type BadgeType = "live" | "sellingFast" | "popular" | "topPick" | "new";

export type EventStatusSlot = "live" | "sellingFast";
export type EventAchievementSlot = "popular" | "topPick" | "new";

const BADGE_LABELS: Record<BadgeType, string> = {
  live: "Live",
  sellingFast: "Selling Fast",
  popular: "Popular",
  topPick: "Top Pick",
  new: "New",
};

export function badgeLabel(type: BadgeType): string {
  return BADGE_LABELS[type];
}

/** ≥ 60% sold — no upper bound (card + detail share this threshold). */
export function isSellingFast(ticketsSold: number, capacity: number): boolean {
  if (capacity <= 0) return false;
  return ticketsSold / capacity >= 0.6;
}

export type EventBadgeInput = {
  live?: boolean;
  popular: boolean;
  isTopPick: boolean;
  savedCount: number;
  ticketsSold: number;
  capacity: number;
  createdAt: string | null;
};

/** Slot A (left): Live > Selling Fast. */
export function getEventStatusSlot(event: EventBadgeInput): EventStatusSlot | null {
  if (event.live) return "live";
  if (isSellingFast(event.ticketsSold, event.capacity)) return "sellingFast";
  return null;
}

/** Slot B (right): Popular > Top Pick > New. */
export function getEventAchievementSlot(
  event: EventBadgeInput,
  badgeRules?: BadgeRuleRow[],
): EventAchievementSlot | null {
  if (event.popular) return "popular";
  if (event.isTopPick) return "topPick";
  const newRule = getBadgeRule(badgeRules, "new", "event");
  if (isEntityNewByRule(event.createdAt, newRule)) return "new";
  return null;
}

/** All matching badges (for filters / flags). Display uses slot helpers instead. */
export function getApplicableBadges(
  event: EventBadgeInput,
  badgeRules?: BadgeRuleRow[],
): BadgeType[] {
  const applicable: BadgeType[] = [];
  if (event.live) applicable.push("live");
  if (isSellingFast(event.ticketsSold, event.capacity)) applicable.push("sellingFast");
  if (event.popular) applicable.push("popular");
  if (event.isTopPick) applicable.push("topPick");
  const newRule = getBadgeRule(badgeRules, "new", "event");
  if (isEntityNewByRule(event.createdAt, newRule)) applicable.push("new");
  return applicable;
}

export function ticketsRemaining(ticketsSold: number, capacity: number): number {
  return Math.max(0, capacity - ticketsSold);
}

export function capacityFillPercent(ticketsSold: number, capacity: number): number | null {
  if (capacity <= 0) return null;
  return Math.min(100, Math.round((ticketsSold / capacity) * 100));
}
